/**
 * Step IDs for the install flow
 */
export type StepId = 'sensor-scan' | 'intro' | 'scan' | 'location' | 'installation' | 'sublocation' | 'details' | 'link' | 'health'

/**
 * Context object passed to isComplete functions
 * Uses URL params as source of truth (survives refresh, shareable)
 */
export interface StepContext {
  // Current step
  currentStep: StepId
  // URL params (from search params, validated by Zod)
  locationId?: number
  sublocationId?: number
  installationId?: number
  installSensorKitSerialNumber?: string
}

/**
 * Centralized configuration for the install flow steps.
 * This is the single source of truth for:
 * - Step sequence and labels
 * - Completion status (has user finished this step?)
 * - Progress indicator rendering
 */
export interface InstallFlowStepConfig {
  id: StepId
  label: string
  /**
   * Determines if the step has been completed based on URL params.
   * Type-safe function that receives URL search params.
   */
  isComplete: (context: StepContext) => boolean
  /**
   * Whether to show this step in the navigation sidebar.
   * Defaults to true if not specified.
   */
  showInNav?: boolean
}

/**
 * All steps in the install flow, in order.
 * Uses URL params as source of truth (survives refresh, shareable).
 */
export const INSTALL_FLOW_STEPS: InstallFlowStepConfig[] = [
  {
    id: 'sensor-scan',
    label: 'Sensor',
    showInNav: false, // Hidden from navigation
    isComplete: (context) => {
      // Sensor scan is complete once we have a sensor serial number in URL
      // or user has moved past this step
      return !!context.installSensorKitSerialNumber || context.currentStep !== 'sensor-scan'
    },
  },
  {
    id: 'intro',
    label: 'Intro',
    showInNav: false, // Hidden from navigation
    isComplete: (context) => {
      // Intro is complete once user has moved to any subsequent step
      return context.currentStep !== 'sensor-scan' && context.currentStep !== 'intro'
    },
  },
  {
    id: 'scan',
    label: 'Scan',
    isComplete: (context) => {
      // Scan is complete once user has moved past scan step
      // (either by uploading or skipping)
      return context.currentStep !== 'sensor-scan' && context.currentStep !== 'intro' && context.currentStep !== 'scan'
    },
  },
  {
    id: 'location',
    label: 'Property',
    isComplete: (context) => {
      // Complete when we have a location ID in URL
      return !!context.locationId
    },
  },
  {
    id: 'installation',
    label: 'Unit',
    isComplete: (context) => {
      // Complete when we have installation ID in URL
      return !!context.installationId
    },
  },
  {
    id: 'sublocation',
    label: 'Location',
    isComplete: (context) => {
      // Complete when we have a sublocation ID in URL
      return !!context.sublocationId
    },
  },
  {
    id: 'details',
    label: 'Details',
    isComplete: (context) => {
      // Complete when installation is created (has ID in URL)
      return !!context.installationId
    },
  },
  {
    id: 'link',
    label: 'Link',
    isComplete: (context) => {
      // Complete when we're on health step
      return context.currentStep === 'health'
    },
  },
  {
    id: 'health',
    label: 'Done',
    isComplete: () => false, // Final step, never completed
  },
]